import * as IF from "infrontjs";
import template from "./detail.html";
import { _lcs, locale } from "../../i18n";

// User status -> German source string. The value is the dictionary key, not the
// finished label: these maps are module constants, evaluated before the app (and
// with it I18n) exists, so the translation has to happen where they are read.
const STATUS_LABELS = {
    ok            : "Aktiv",
    not_validated : "Nicht verifiziert",
    blocked       : "Gesperrt",
    banned        : "Gebannt"
};

// Role key -> display label.
const ROLE_LABELS = {
    admin  : "Admin",
    client : "Client",
    guest  : "Gast"
};

/**
 * <admin-user-detail> — read-only modal showing the details of an admin
 * (platform operator) user.
 *
 * Usage:
 *   el.open( user )
 */
class AdminUserDetail extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );
        this._modal = this.querySelector( '[data-id="aud-modal"]' );
    }

    bind()
    {
        this.querySelector( '[data-id="aud-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="aud-close-btn"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="aud-backdrop"]' ).addEventListener( "click", () => this.close() );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    open( user )
    {
        if ( !user ) return;

        this._set( "aud-name", this._displayName( user ) || "—" );
        this._set( "aud-email", user.email || "—" );
        this._html( "aud-roles", this._rolesBadgesHtml( user.role ) );
        this._set( "aud-status", this._statusLabel( user.status ) );
        this._set( "aud-created", this._formatDate( user.createdAt ) );
        this._set( "aud-updated", this._formatDate( user.updatedAt ) );
        this._set( "aud-id", user.id || "—" );

        this._modal.hidden = false;
        this.querySelector( '[data-id="aud-close-btn"]' ).focus();
    }

    close()
    {
        this._modal.hidden = true;
    }

    /* ------------------------------- Helpers -------------------------------- */

    _set( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.textContent = value;
    }

    _html( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.innerHTML = value;
    }

    _displayName( user )
    {
        return ( user && user.data && user.data.name ) ? String( user.data.name ) : "";
    }

    _statusLabel( status )
    {
        return STATUS_LABELS[ status ] ? _lcs( STATUS_LABELS[ status ] ) : ( status || "—" );
    }

    /**
     * Render the role value (array or comma-separated string) as colored badges.
     */
    _rolesBadgesHtml( value )
    {
        const roles = Array.isArray( value )
            ? value
            : String( value || "" ).split( "," ).map( ( r ) => r.trim() ).filter( Boolean );

        if ( roles.length === 0 )
        {
            return '<span class="role-badge role-badge--none">—</span>';
        }

        const badges = roles.map( ( r ) =>
        {
            const key = String( r ).toLowerCase();
            const known = ROLE_LABELS.hasOwnProperty( key );
            const label = known ? this._escape( _lcs( ROLE_LABELS[ key ] ) ) : this._escape( r );
            const mod = known ? key : "other";
            return '<span class="role-badge role-badge--' + mod + '">' + label + '</span>';
        } );

        return '<span class="role-badges">' + badges.join( "" ) + '</span>';
    }

    _escape( str )
    {
        return String( str == null ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    _formatDate( value )
    {
        if ( !value ) return "—";
        const d = new Date( value );
        if ( isNaN( d.getTime() ) ) return String( value );
        return new Intl.DateTimeFormat( locale(), {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        } ).format( d );
    }
}

customElements.define( "admin-user-detail", AdminUserDetail );
